import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { FormattedDate } from 'react-intl';

import Footer from '../../components/Footer';
import Message from '../../components/Message';
import SiteHeaderContainer from '../../components/SiteHeader';
import ImpersonatorBanner from '../ImpersonatorBanner';
import IdleTimeout from '../IdleTimeout';
import Nav from '../../components/Nav';
import InProdNotifications from '../InProdNotifications';
import NavianceSyncMessages from '../NavianceSyncMessage';
import SidebarWrapper from '../../components/SidebarWrapper';
import InlineManual from '../InlineManual';
import FeatureEnabled from '../../components/FeatureEnabled';
import * as constants from '../Authorization/constants';
import intersectLogo from './counselor-community-by-hobsons-rgb-black@3x.png';
import './styles.css';

class AppWithoutServerlessCounselorCommunity extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    idleTimeout: PropTypes.number,
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        isAuthenticated: PropTypes.bool,
      }),
    }),
    history: PropTypes.shape({
      listen: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.object.isRequired,
  };

  state = {
    sidebarVisible: false,
    announcement: {},
    hasError: false,
  };

  componentDidMount() {
    this.routerListener = this.props.history.listen(() => this.setState({ hasError: false }));
  }

  componentWillUnmount() {
    this.routerListener();
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ hasError: true });
    if (window.Sentry) {
      window.Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);
        window.Sentry.captureException(error);
      });
    }
  }

  handleButtonClick = (readmoreData) => {
    this.setState((prevState) => ({ announcement: readmoreData, sidebarVisible: !prevState.sidebarVisible }));
  }

  closeNotificationBar = () => {
    /* istanbul ignore else */
    if (this.state.sidebarVisible) {
      this.setState({ sidebarVisible: false });
    }
  };

  render() {
    const { children, idleTimeout, current } = this.props;
    const { sidebarVisible, announcement, hasError } = this.state;
    const isAuthenticated = get(current, 'viewer.isAuthenticated') || false;
    const isNavianceLogin = get(current, 'viewer.isNaviance') || false;

    return (
      <div>
        <SidebarWrapper.Content notPaddedDimmed onClick={this.closeNotificationBar}>
          {!isAuthenticated && (
            <div styleName="wrapper">
              <div styleName="main">
                <header styleName="header" role="banner">
                  <SiteHeaderContainer />
                </header>
                <main styleName="content">
                  {hasError ? <Message error header="Oops, something went wrong" /> : children}
                </main>
                <footer styleName="footer">
                  <Footer />
                </footer>
              </div>
            </div>
          )}
          {isAuthenticated && (
            <React.Fragment>
              <IdleTimeout timeout={(idleTimeout || 1800) * 1000} />
              <div styleName="wrapper">
                <div styleName="main">
                  <ImpersonatorBanner />
                  <NavianceSyncMessages
                    displayNavianceSyncMessage={this.displayNavianceSyncMessage}
                    hideNavianceSyncMessage={this.hideNavianceSyncMessage}
                  >
                    <InProdNotifications onReadMoreClick={this.handleButtonClick} />
                  </NavianceSyncMessages>
                  <header styleName="headerNav" role="banner">
                    <div styleName="leftAlign">
                      <Link styleName="logo" to="/" as="div">
                        <img
                          src={intersectLogo}
                          styleName="verticalAlign"
                          alt="Intersect Logo"
                        />
                      </Link>
                      <Nav className="hidden-mobile hidden-tablet print" styleName="print" />
                    </div>
                    <SiteHeaderContainer authenticated={isAuthenticated} isNavianceLogin={isNavianceLogin} />
                  </header>
                  <main styleName="content">
                    {hasError ? <Message error header="Oops, something went wrong" /> : children}
                  </main>
                  <footer styleName="footer">
                    <Footer />
                  </footer>
                </div>
              </div>
              <FeatureEnabled allOf={constants.INLINE_MANUAL}>
                <InlineManual viewer={get(current, 'viewer')} />
              </FeatureEnabled>
            </React.Fragment>
          )}
        </SidebarWrapper.Content>
        <SidebarWrapper.Bar
          visible={sidebarVisible}
          onClose={this.closeNotificationBar}
          styleName="padded-content"
        >
          <div styleName="announcement">
            <div styleName="announcement-header">Product Announcement</div>
            <div styleName="announcement-title">
              {announcement.title}
              {announcement.createdDate && (
                <span styleName="announcement-date">
                  <FormattedDate value={announcement.createdDate} month="short" day="2-digit" />
                </span>
              )}
            </div>
            <div styleName="announcement-content">{announcement.content}</div>
          </div>
        </SidebarWrapper.Bar>
      </div>
    );
  }
}

export default compose(
  withRouter,
  connect((state) => ({ current: state.current })),
)(AppWithoutServerlessCounselorCommunity);
